'use client';

import {
  type SiteRuntimeWorkspaceToolResponse,
  parseSiteRuntimeWorkspaceToolResponse,
} from '@/site-runtime/workspace-tool-runtime-response';
import {
  WORKSPACE_TOOL_RUNTIME_PATH_PREFIX,
  type WorkspaceStudioRenderModel,
} from './workspace-1/contracts/server-model';

type WorkspaceToolResource = SiteRuntimeWorkspaceToolResponse & {
  model: WorkspaceStudioRenderModel;
};
type ResourceParams = { toolId: string; locale: string; pathPrefix?: string };
const resources = new Map<string, Promise<WorkspaceToolResource>>();

function getWorkspaceToolResourceUrl({
  toolId,
  locale,
  pathPrefix = WORKSPACE_TOOL_RUNTIME_PATH_PREFIX,
}: ResourceParams) {
  return `${pathPrefix}/${encodeURIComponent(toolId)}/resource/${encodeURIComponent(locale)}`;
}

export function loadWorkspaceToolResource({
  toolId,
  locale,
  pathPrefix = WORKSPACE_TOOL_RUNTIME_PATH_PREFIX,
}: ResourceParams) {
  const url = getWorkspaceToolResourceUrl({ toolId, locale, pathPrefix });
  const existing = resources.get(url);
  if (existing) return existing;
  const promise = (async () => {
    const response = await fetch(url, {
      credentials: 'omit',
      priority: 'high',
    });
    if (!response.ok) throw new Error('Failed to load Workspace Tool resource');
    const payload = (await response.json()) as WorkspaceToolResource;
    if (
      !payload?.model ||
      payload.model.locale !== locale ||
      payload.model.initialAction?.initialToolId !== toolId
    ) {
      throw new Error('Workspace Tool model identity is invalid');
    }
    return payload;
  })().catch((error) => {
    if (resources.get(url) === promise) resources.delete(url);
    throw error;
  });
  resources.set(url, promise);
  return promise;
}

export async function loadFreshWorkspaceToolVersion(params: ResourceParams) {
  const response = await fetch(getWorkspaceToolResourceUrl(params), {
    cache: 'no-store',
    credentials: 'omit',
  });
  if (!response.ok) {
    throw new Error('Failed to check Workspace Tool version');
  }
  const payload = (await response.json()) as WorkspaceToolResource;
  const parsed = parseSiteRuntimeWorkspaceToolResponse(
    {
      runtimePackage: payload.runtimePackage,
      startup: payload.startup,
      toolPresentation: payload.toolPresentation,
    },
    params.toolId,
    params.locale
  );
  if (!parsed) {
    throw new Error('Workspace Tool version response is invalid');
  }
  return parsed.runtimePackage.toolVersionId;
}

export async function loadWorkspaceToolRuntime(params: ResourceParams) {
  const promise = loadWorkspaceToolResource(params);
  const payload = await promise;
  const parsed = parseSiteRuntimeWorkspaceToolResponse(
    {
      runtimePackage: payload.runtimePackage,
      startup: payload.startup,
      toolPresentation: payload.toolPresentation,
    },
    params.toolId,
    params.locale
  );
  if (!parsed) {
    const prefix = params.pathPrefix ?? WORKSPACE_TOOL_RUNTIME_PATH_PREFIX;
    const url = `${prefix}/${encodeURIComponent(params.toolId)}/resource/${encodeURIComponent(params.locale)}`;
    if (resources.get(url) === promise) resources.delete(url);
    throw new Error('Workspace Tool runtime response is invalid');
  }
  return parsed;
}

export async function loadWorkspaceToolModel(
  params: ResourceParams & { surface: WorkspaceStudioRenderModel['surface'] }
) {
  const { model } = await loadWorkspaceToolResource(params);
  return { ...model, surface: params.surface };
}

export function resetWorkspaceToolResourceCacheForTests() {
  resources.clear();
}
